import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { appendGroupIdParam, applyAnalyticsPendingLoading, markAnalyticsFetchKey } from '../../utils/analyticsQuery';
import dayjs from 'dayjs';

// Async thunk for fetching chat analytics stats
export const getChatAnalyticsStats = createAsyncThunk(
  'chatAnalyticsStats/getChatAnalyticsStats',
  async ({ range, startDate, endDate, group_id }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      if (!businessId) {
        throw new Error('Business ID not found');
      }

      let url = `${ROOT_URL}/analytics/chat-stats/${businessId}`;
      let params = new URLSearchParams();
      
      // Add timezone parameter
      params.append('timezone', timezone);
      
      // Handle different range types
      if (range === 'custom' && startDate && endDate) {
        params.append('range', 'custom');
        params.append('from', startDate);
        params.append('to', endDate);
      } else {
        // Map 365d to 1y for API compatibility
        const apiRange = range === '365d' ? '1y' : range;
        params.append('range', apiRange);
      }

      appendGroupIdParam(params, group_id);
      
      url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat analytics stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch chat analytics stats'
      );
    }
  }
);

// Initial state
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  metrics: {
    totalChats: { value: 0, change: 0 },
    queuedChats: { value: 0, change: 0 },
    missedChats: { value: 0, change: 0 },
    avgResponseTime: { value: 0, change: 0 },
    avgChatDuration: { value: 0, change: 0 }
  },
  range: null,
  from: null,
  to: null
};

// Create slice
const chatAnalyticsStatsSlice = createSlice({
  name: 'chatAnalyticsStats',
  initialState,
  reducers: {
    resetChatAnalyticsStats: (state) => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // getChatAnalyticsStats cases
      .addCase(getChatAnalyticsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getChatAnalyticsStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        state.data = action.payload.data;
        state.metrics = action.payload.data.metrics;
        state.range = action.payload.data.range;
        state.from = action.payload.data.from;
        state.to = action.payload.data.to;
      })
      .addCase(getChatAnalyticsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
      });
  }
});

// Export actions
export const { resetChatAnalyticsStats, clearError } = chatAnalyticsStatsSlice.actions;

// Export reducer
export default chatAnalyticsStatsSlice.reducer;
